import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "../lib/api";
import { authClient, looksLikeWrongDoor } from "../lib/auth";
import type { ProfileCard, ProfilePatch, ProfileVisibility } from "@pagemates/shared";
import { BackBar, Confirm, Spinner } from "../components/ui";
import { Group, Saved } from "./club/ClubSettingsPage";
import { cameFrom } from "../lib/returnTo";

import { WaysToSignIn, WrongDoorNote } from "../components/SignIn";
import { useTheme, type Theme } from "../lib/theme";
import { NotificationSettings } from "../components/NotificationSettings";
import { EmailAddresses } from "../components/Emails";

/** Dark, light, or whatever the device says. Per browser; nothing is stored on the server. */
function Appearance() {
  const [theme, setTheme] = useTheme();
  return (
    <section className="card">
      <div className="switch stacked">
        <div><div className="k">Appearance</div><div className="d">This browser only. System follows your device.</div></div>
        <div className="seg" role="group" aria-label="Appearance">
          {(["system", "dark", "light"] as Theme[]).map((t) => (
            <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>{{ system: "System", dark: "Dark", light: "Light" }[t]}</button>
          ))}
        </div>
      </div>
    </section>
  );
}
function HardcoverCard({ me }: { me: import("@pagemates/shared").Me }) {
  const qc = useQueryClient();
  const { hardcover: outcome } = useSearch({ from: "/authed/me/settings" });
  const hc = me.hardcover;
  const sync = useMutation({ mutationFn: () => api.hardcoverSync(true), onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });
  const unlink = useMutation({ mutationFn: api.hardcoverUnlink, onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }) });
  if (!hc.available) return null;

  return (
    <div className="card stack">
      <div className="row spread"><h3>Hardcover</h3>{hc.linked && <span className={`pill${hc.status === "ok" ? " accent" : ""}`}>{hc.status === "ok" ? "connected" : "needs reconnect"}</span>}</div>
      {outcome === "linked" && <p className="ok">Connected. Your progress will sync from Hardcover from now on.</p>}
      {outcome === "denied" && <p className="error">You cancelled on Hardcover's side. Nothing was connected.</p>}
      {outcome === "error" && <p className="error">That didn't go through. Try connecting again.</p>}
      {!hc.linked ? (
        <>
          <p className="muted small">Connect your Hardcover account and your reading progress on club books syncs on its own. Pagemates only asks to read your library.</p>
          <a className="btn primary" href="/api/hardcover/connect">Connect Hardcover</a>
        </>
      ) : (
        <>
          <p className="muted small">
            Linked as <strong>{hc.username}</strong>.{" "}
            {hc.status === "broken" ? "Hardcover stopped accepting our access, so syncing has paused." : hc.lastSyncAt ? `Last synced ${new Date(hc.lastSyncAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.` : "Not synced yet."}
          </p>
          <div className="row" style={{ flexWrap: "wrap" }}>
            {hc.status === "broken" ? (
              <a className="btn primary sm" href="/api/hardcover/connect">Reconnect</a>
            ) : (
              <button className="btn sm" disabled={sync.isPending} onClick={() => sync.mutate()}>{sync.isPending ? "Syncing…" : "Sync now"}</button>
            )}
            <Confirm label="Disconnect" className="ghost sm" busy={unlink.isPending} onConfirm={() => unlink.mutate()}
              question="Disconnect Hardcover? Progress already synced stays; nothing new comes in until you reconnect." />
          </div>
          {sync.data && <p className="muted small">{sync.data.synced ? `Synced. ${sync.data.updated} update${sync.data.updated === 1 ? "" : "s"}.` : "Nothing to sync right now."}</p>}
          {(sync.isError || unlink.isError) && <p className="error">{describeError(sync.error ?? unlink.error)}</p>}
        </>
      )}
    </div>
  );
}

/** Who can see your card, and what of it (PLAN.md §Profiles as library cards). */
function CardSettings({ card }: { card: ProfileCard }) {
  const qc = useQueryClient();
  const own = card.own!;
  const [asking, setAsking] = useState(false);
  const [showToo, setShowToo] = useState(own.showOtherClubBooks);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (p: ProfilePatch) => api.updateProfile(p),
    onSuccess: async (_d, p) => {
      await qc.invalidateQueries({ queryKey: ["profile"] });
      setAsking(false);
      setSaved(p.visibility ? "visibility" : "other"); window.setTimeout(() => setSaved(null), 1800);
    },
  });
  const choose = (v: ProfileVisibility) => {
    if (v === own.visibility) return;
    if (v === "link") { setShowToo(own.showOtherClubBooks); setAsking(true); return; }
    setAsking(false); set.mutate({ visibility: v });
  };
  const link = own.slug ? `${window.location.origin}/p/${own.slug}` : null;
  const opts: { key: ProfileVisibility; label: string; line: string }[] = [
    { key: "clubs", label: "Shared clubs", line: "People in a club with you." },
    { key: "private", label: "Only me", line: "Clubmates see your name, photo and what you're reading together." },
    { key: "link", label: "Anyone with the link", line: "Clubmates, and anyone you send your card's link to." },
  ];
  const { all, inClubs } = own.stampCounts;
  return (
    <>
      <Group title="Who can see your card" about="Your name and photo always show in clubs you're in.">
        <div className="field"><label>Visibility<Saved on={saved === "visibility"} /></label>
          <div className="vis-list" role="radiogroup" aria-label="Who can see your card">
            {opts.map((o) => (
              <button key={o.key} type="button" role="radio" aria-checked={own.visibility === o.key} className={`vis-opt${own.visibility === o.key ? " on" : ""}`} onClick={() => choose(o.key)}>
                <span className="radio-dot" aria-hidden="true" /><span><b>{o.label}</b><span className="d">{o.line}</span></span>
              </button>
            ))}
          </div>
        </div>
        {asking && (
          <div className="vis-confirm card" role="alertdialog" aria-label="Confirm">
            <b>Let anyone with the link see your card?</b>
            <p className="muted small">Anyone you send it to, signed in or not, will see:</p>
            <ul className="allow">
              <li>Your name, photo, line about you and genres</li>
              <li>When your card was issued, and your Hardcover link</li>
              <li>Your pinned quote, sealed until they've read past it</li>
            </ul>
            {!own.showOtherClubBooks && (
              <>
                <p className="small">Your stamps come from your clubs, and someone with the link isn't in them, so <b>your card will look empty to them</b> unless you show books from other clubs.</p>
                <div className="switch">
                  <div><div className="k">Show my stamps</div><div className="d">Without club names.</div></div>
                  <input className="toggle" type="checkbox" checked={showToo} aria-label="Show my stamps to people with the link" onChange={(e) => setShowToo(e.target.checked)} />
                </div>
              </>
            )}
            <p className="muted small">Never shown: which clubs you're in, or what you're reading now.</p>
            {set.isError && <p className="error small">{describeError(set.error)}</p>}
            <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost sm" onClick={() => setAsking(false)}>Cancel</button>
              <button type="button" className="btn primary sm" disabled={set.isPending}
                onClick={() => set.mutate({ visibility: "link", ...(showToo !== own.showOtherClubBooks ? { showOtherClubBooks: showToo } : {}) })}>Turn on</button>
            </div>
          </div>
        )}
        {own.visibility === "link" && link && (
          <div className="switch">
            <div style={{ minWidth: 0 }}><div className="k">Your card's link</div><div className="d" style={{ overflowWrap: "anywhere" }}>{link.replace(/^https?:\/\//, "")}</div></div>
            <button type="button" className="btn sm" onClick={() => { void navigator.clipboard?.writeText(link).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1800); }); }}>{copied ? "Copied" : "Copy link"}</button>
          </div>
        )}
      </Group>
      <Group title="Books from other clubs" about="Stamps from clubs the person looking isn't in. Your own card always shows everything.">
        <div className="field"><label>Stamps<Saved on={saved === "other"} /></label>
          <div className="seg" role="group" aria-label="Books from other clubs">
            <button type="button" aria-pressed={!own.showOtherClubBooks} onClick={() => own.showOtherClubBooks && set.mutate({ showOtherClubBooks: false })}>Hidden</button>
            <button type="button" aria-pressed={own.showOtherClubBooks} onClick={() => !own.showOtherClubBooks && set.mutate({ showOtherClubBooks: true })}>Shown without club names</button>
          </div>
        </div>
        {all > inClubs && <p className="muted small" style={{ margin: 0 }}>{own.showOtherClubBooks
          ? <>Clubmates see up to <b>{all}</b> stamps, not just the <b>{inClubs}</b> from clubs you're in.</>
          : <>Clubmates will see up to <b>{all}</b> stamps instead of <b>{inClubs}</b>.</>}</p>}
      </Group>
    </>
  );
}

export function SettingsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const card = useQuery({ queryKey: ["profile", "me"], queryFn: () => api.profile("me") });
  const signOut = useMutation({
    mutationFn: () => authClient.signOut(),
    onSuccess: () => { qc.clear(); void navigate({ to: "/", replace: true }); },
  });
  const del = useMutation({
    mutationFn: api.deleteMe,
    onSuccess: () => { qc.clear(); void navigate({ to: "/", replace: true }); },
  });
  const { linked: linkedParam } = useSearch({ from: "/authed/me/settings" });

  if (me.isPending) return <main className="page"><Spinner /></main>;
  if (me.isError || !me.data) return <main className="page"><p className="error">{describeError(me.error)}</p></main>;
  const u = me.data;
  const wrongDoor = looksLikeWrongDoor(u);

  return (
    <main className="page stack settings me-settings">
      <BackBar label="Your card" link={{ to: "/me" }} onClick={(e) => { if (cameFrom("/me")) { e.preventDefault(); window.history.back(); } }} />
      <h1>Settings</h1>
      <div className="sgroups">
        {card.data?.own && <CardSettings card={card.data} />}
        <NotificationSettings />
        {/* Everything that was on the profile page before the card, as it was. */}
        <section className="sgroup">
          <header><h2>Account</h2><p>How you sign in, Hardcover, and how the app looks on this browser.</p></header>
          <div className="stack">
            {wrongDoor && <WrongDoorNote me={u} />}
            <WaysToSignIn me={u} justLinked={linkedParam} />
            <EmailAddresses />
            <HardcoverCard me={u} />
            <Appearance />
            <div className="stack-sm">
              <button className="btn" onClick={() => signOut.mutate()} disabled={signOut.isPending}>Sign out</button>
              <Confirm label="Delete my account" busy={del.isPending} onConfirm={() => del.mutate()}
                question="This removes your progress, comments, ratings and your library card from every club. Clubs you own go to their longest-standing admin or member. There is no undo." />
              {del.isError && <p className="error">{describeError(del.error)}</p>}
            </div>
          </div>
        </section>
      </div>
      <p className="muted small center"><a href="/policy.html">Policy and contact</a></p>
    </main>
  );
}
